import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  model,
  Output,
  Renderer2,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import {EControlActions, EsvgFiles, IApiControl, IControlDataChanged, IControlObject} from 'frontier/nucleus';
import {SignalControl} from 'frontier/nucleus/src/lib/control/base-control-new/signal-control.directive';
import {IFilter} from 'frontier/nucleus/src/lib/control/interfaces/filter.interface';
import {
  IDataField,
  IInvoiceTemplate,
  IInvoiceTemplates,
  ITemplateConfigurationDto,
  ITemplateConfigurationFormValue,
  ITemplateModificationDto,
} from './template.interface';
import {FormBuilder, FormControl, FormsModule} from '@angular/forms';
import {TemplatesEditorMapper} from './templates-editor.mapper';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {TemplatesFormComponent} from './templates-form.component.ts/templates-form.component';
import {MatDialog} from '@angular/material/dialog';
import {
  ConfirmationDialogComponent,
  EntitySelectorComponent,
  FeedbackService,
  ISelectFormOption,
  SelectElementComponent,
} from 'frontier/browserkit';
import {MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup} from '@angular/material/button-toggle';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {AsyncPipe, NgIf} from '@angular/common';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {MatButton} from '@angular/material/button';
import {SplitterModule} from 'primeng/splitter';
import AppControlGuids from '../../../api/ControlGuids';
import {LanguageCheckService, TDBObject, TemplateConfigurationControlService} from '../../../api/generated';
import {toSignal} from '@angular/core/rxjs-interop';
import {ISelectableEntity} from 'frontier/browserkit/src/lib/dialogs/entity-selector/selectable-entity.interface';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {filter, tap} from 'rxjs/operators';
import ControlGuids from '../../../api/ControlGuids';
import {AlertService} from '../language-config/alert.service';
import {TabsService} from 'frontier/browserkit/src/lib/components/home-without-gl/tabs/tabs.service';
import {ITab} from 'frontier/browserkit/src/lib/components/home-without-gl/tabs/tab.interface';
import {InvoiceAutomationTabService} from './invoice-automation-tab.service';
import {InvoiceAutomationApiAdapter, InvoiceAutomationEditorComponent} from './invoice-automation-editor.component';

export interface ILanguageSelectFormOption extends ISelectFormOption {
  usedefault: boolean;
  configured: boolean;
}

export interface ILanguageSelectObject extends IControlObject {
  usedefault: boolean;
  configured: boolean;
}

@Component({
  selector: 'app-templates-control',
  templateUrl: './templates-editor.component.html',
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatSlideToggle,
    FormsModule,
    TemplatesFormComponent,
    MatButton,
    SplitterModule,
    SelectElementComponent,
    MatIcon,
    MatTooltip,
  ],
  styleUrl: './templates-editor.component.scss',
})
export class TemplatesEditorComponent extends SignalControl<IInvoiceTemplates> {
  private _alertService: AlertService = inject(AlertService);
  private _tabsService: TabsService = inject(TabsService);
  private _invoiceAutomationTabService: InvoiceAutomationTabService = inject(InvoiceAutomationTabService);

  alertObjects: Signal<IControlObject[]> = this._alertService.invoiceAlertObjects.asReadonly();

  @Output() defaultLanguageConfigured: EventEmitter<void> = new EventEmitter<void>();

  protected _templateConfigurationControlService: TemplateConfigurationControlService
    = inject(TemplateConfigurationControlService);
  protected _languageCheckService: LanguageCheckService = inject(LanguageCheckService);
  protected _feedbackService: FeedbackService = inject(FeedbackService);
  cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  fb: FormBuilder = inject(FormBuilder);
  override GUID: string = AppControlGuids.TTemplateConfigurationControl;

  pdfSrc$: BehaviorSubject<SafeResourceUrl> = new BehaviorSubject(null);
  overlayElement: Signal<ElementRef> = viewChild<ElementRef>('overlayElement');
  languageSelectRef: Signal<SelectElementComponent> = viewChild<SelectElementComponent>('languageSelectRef');

  dialog: MatDialog = inject(MatDialog);

  languageFormControl: FormControl<TDBObject> = new FormControl<TDBObject>(null);
  previousLanguage: TDBObject = null;
  configuredLanguages: WritableSignal<ILanguageSelectFormOption[]> = signal<ILanguageSelectFormOption[]>([]);
  unconfiguredLanguages: WritableSignal<ILanguageSelectFormOption[]> = signal<ILanguageSelectFormOption[]>([]);
  languageSignal: Signal<TDBObject> = toSignal<TDBObject>(this.languageFormControl.valueChanges);

  dataFields: WritableSignal<IDataField[]> = signal(null);
  standardFormRef: Signal<TemplatesFormComponent | undefined>
    = viewChild<TemplatesFormComponent>('standardFormRef');
  displayedFormRef: Signal<TemplatesFormComponent | undefined>
    = viewChild<TemplatesFormComponent>('displayedFormRef');

  selectedInvoice: WritableSignal<IInvoiceTemplate | 1> = model<IInvoiceTemplate | 1>(1);
  previousInvoice: IInvoiceTemplate | 1 = this.selectedInvoice();
  languageReposLoaded: WritableSignal<boolean> = signal<boolean>(false);
  protected isInvoice: boolean = true;
  protected isCancellationConfig: WritableSignal<boolean> = signal<boolean>(false);
  standardForm: WritableSignal<ITemplateConfigurationFormValue> = signal(null);
  displayedForm: WritableSignal<ITemplateConfigurationFormValue> = signal(null);

  isStandardFormSelected: Signal<boolean> = computed((): boolean => this.selectedInvoice() === 1);

  protected sanitizer: DomSanitizer = inject(DomSanitizer);
  protected readonly EsvgFiles: typeof EsvgFiles = EsvgFiles;
  protected standardConfigDto: WritableSignal<ITemplateConfigurationDto>
    = signal<ITemplateConfigurationDto>(null);
  private renderer: Renderer2 = inject(Renderer2);


  constructor() {
    super();

    effect((): void => {
      const apiInstance: IApiControl = this.apiInstance();
      const isCancellationConfig: boolean = this.isCancellationConfig();
      const language: TDBObject = this.languageSignal();
      const selectedInvoice: IInvoiceTemplate | 1 = this.selectedInvoice();

      if (!apiInstance?.instanceid || selectedInvoice !== 1) return;

      this.fetchInvoiceConfiguration<ITemplateConfigurationDto>(isCancellationConfig, language)
        .subscribe((configDto: ITemplateConfigurationDto): void => {
          this.standardConfigDto.set(configDto);
          this.standardForm.set(TemplatesEditorMapper.toModel(configDto));
          if (configDto) {
            this._templateConfigurationControlService.templateConfigurationControlGetDataFields()
              .subscribe((res: any): void => {
                this.dataFields.set(res);
              })
          }
        });
    }, {allowSignalWrites: true});

    effect((): void => {
      let standardConfigDto: ITemplateConfigurationDto = this.standardConfigDto();
      const apiInstance: IApiControl = this.apiInstance();
      const isCancellationConfig: boolean = this.isCancellationConfig();
      const language: TDBObject = this.languageSignal();
      const selectedInvoice: IInvoiceTemplate | 1 = this.selectedInvoice();
      const isLanguageReposLoaded: boolean = this.languageReposLoaded();

      if (!apiInstance?.instanceid || this.isStandardFormSelected() || !isLanguageReposLoaded) return;

      this.fetchInvoiceConfiguration<ITemplateModificationDto>(
        isCancellationConfig,
        language,
        selectedInvoice as IInvoiceTemplate,
      )
        .subscribe((dto: ITemplateModificationDto): void => {
          if (this.previousInvoice != selectedInvoice && !this.isInvoice) {
            if (dto) {
              this.getDataFields();
            }

            this.previousInvoice = selectedInvoice;
          }

          if (!standardConfigDto || !dto) return;
          // Copy the standardConfigDto to prevent changes to the original object
          const standardConfigDtoCopy: ITemplateConfigurationDto = JSON.parse(JSON.stringify(standardConfigDto));

          this.displayedForm.set(TemplatesEditorMapper.toSpecificModel(standardConfigDtoCopy, dto));
          this.cdr.detectChanges();
        });
    }, {allowSignalWrites: true});

    effect((): void => {
      const selectedInvoice: IInvoiceTemplate | 1 = this.selectedInvoice();
      if (selectedInvoice != 1) {
        this.requestLanguageRepos(selectedInvoice);
      } else {
        this.configuredLanguages.set([]);
        this.unconfiguredLanguages.set([]);
        this.languageReposLoaded.set(true);
      }
    }, {allowSignalWrites: true});

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [ControlGuids.TLanguageTable, new Set([
              EControlActions.edit,
              EControlActions.delete,
              EControlActions.create,
              EControlActions.changeLine,
            ])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((): void => {
          const selectedInvoice: IInvoiceTemplate | 1 = this.selectedInvoice();
          if (selectedInvoice != 1) {
            this.requestLanguageRepos(selectedInvoice);
          } else {
            this.configuredLanguages.set([]);
            this.unconfiguredLanguages.set([]);
          }

          if (this.isInvoice) {
            this._languageCheckService.languageCheckGetChangesNeededForInvoices()
              .subscribe((res: TDBObject[]): void => this._alertService.invoiceAlertObjects.set(res));
          }
        })).subscribe(),
    );

    this.subs.add(
      this.languageFormControl.valueChanges.pipe(
        filter((): boolean => !(this.displayedFormRef()?.form.dirty))
      ).subscribe((val: TDBObject): void => {
        this.previousLanguage = val;
      })
    );
  }

  protected getDataFields(): void {
    this._templateConfigurationControlService.templateConfigurationControlGetDataFields()
      .subscribe((res: any): void => {
        this.dataFields.set(res);
      });
  }

  protected requestLanguageRepos(selectedInvoice: IInvoiceTemplate | 1): void {
    this._templateConfigurationControlService
      .templateConfigurationControlGetRepository({TemplateConfigObj: selectedInvoice as TDBObject})
      .subscribe((res: ILanguageSelectObject[]): void => {
        console.log(res);
        this.updateLanguageRepos(res.map((item: ILanguageSelectObject): ILanguageSelectFormOption => {
          return {
            ...item,
            value: item,
          } as ILanguageSelectFormOption;
        }));
      });
  }

  protected updateLanguageRepos(languages: ILanguageSelectFormOption[]): void {
    this.configuredLanguages.set(languages.filter((el: ILanguageSelectFormOption): boolean => el.configured));
    this.unconfiguredLanguages.set(languages.filter((el: ILanguageSelectFormOption): boolean => !el.configured));

    if (this.configuredLanguages().length > 0) {
      const selectedLanguage: ILanguageSelectFormOption = this.configuredLanguages()
        .find((el: ILanguageSelectFormOption): boolean => el.signature === this.languageSignal()?.signature);
      const defaultLanguage: ILanguageSelectFormOption = this.configuredLanguages()
        .find((el: ILanguageSelectFormOption): boolean => el.usedefault);
      this.languageFormControl.setValue(selectedLanguage || defaultLanguage || this.configuredLanguages()[0]);
    }
    this.languageReposLoaded.set(true);
  }

  protected fetchInvoiceConfiguration<T extends ITemplateConfigurationDto | ITemplateModificationDto>(
    isCancellationConfig: boolean,
    language: TDBObject,
    templateConfig?: IInvoiceTemplate,
  ): Observable<T> {
    if (!this.apiInstance()?.instanceid) return of(null);
    if (templateConfig && !language) return of(null);
    if (language && !this.configuredLanguages().map((l: ILanguageSelectFormOption): string => l.signature)
      .includes(language.signature)) return of(null);
    this.refreshPreview(isCancellationConfig, language, templateConfig);
    return this._templateConfigurationControlService.templateConfigurationControlGetConfiguration({
      TemplateConfigObj: templateConfig ? templateConfig : null,
      IsCancellationConfig: isCancellationConfig,
      Language: language || null,
    })
  }

  protected refreshPreview(isCancellationConfig: boolean, language: TDBObject, templateConfig: IInvoiceTemplate): void {
    if (templateConfig && !language) return;
    this._templateConfigurationControlService.templateConfigurationControlGeneratePdfPreview({
      IsCancellationConfig: isCancellationConfig,
      TemplateConfigObj: templateConfig ? templateConfig : null,
      Language: language || null,
    }).subscribe({
      next: (res: Blob): void => {
        const url: string = URL.createObjectURL(res);
        this.pdfSrc$.next(this.sanitizer.bypassSecurityTrustResourceUrl(url));
      },
      error: (): void => {
        this._feedbackService.setError('Beim Generieren der PDF ist ein unbekannter Fehler aufgetreten.');
        this.pdfSrc$.next(null);
      }
    })
  }

  saveChanges(): void {
    const form: TemplatesFormComponent
      = (this.isStandardFormSelected() ? this.standardFormRef() : this.displayedFormRef()) as TemplatesFormComponent;

    const formValue: ITemplateConfigurationFormValue = (this.isStandardFormSelected() ?
      this.standardFormRef().form.value :
      this.displayedFormRef().form.value) as unknown as ITemplateConfigurationFormValue;

    let dtoObj: ITemplateConfigurationDto | ITemplateModificationDto;
    if (this.isStandardFormSelected()) {
      dtoObj = TemplatesEditorMapper.toDefaultDto(formValue);
    } else {
      const diff: any = this.getDifference(
        this.displayedFormRef().form.value as ITemplateConfigurationFormValue,
        this.standardForm()
      );
      if (formValue.dunning) {
        diff.dunning = formValue.dunning;
      }
      // Always send these fields: the backend treats a missing attribute as unchanged, so a value
      // (e.g. `false` or `0`) must not be dropped just because it equals the standard value.
      diff.period = formValue.period;
      diff.charge = formValue.charge;
      diff.defaultinterest = formValue.defaultinterest;
      diff.account = formValue.account;
      diff.costlocation = formValue.costlocation;
      diff.delay = formValue.delay;
      diff.autoarrange = formValue.autoarrange ?? false;
      diff.autocreate = formValue.autocreate ?? false;
      diff.iscustomdunning = formValue.iscustomdunning ?? false;
      dtoObj = TemplatesEditorMapper.toSpecificDto(diff, this.isInvoice);
    }
    const selectedInvoice: 1 | IInvoiceTemplate = this.selectedInvoice();

    if (form.form.valid) {
      this.performSaveConfiguration(dtoObj, selectedInvoice).subscribe((res: boolean): void => {
        if (res) {
          this.displayedFormRef()?.form.markAsPristine();
          this.standardFormRef()?.form.markAsPristine();
          this.refreshPreview(
            this.isCancellationConfig(),
            this.languageFormControl.value,
            this.isStandardFormSelected() ? null : selectedInvoice as IInvoiceTemplate,
          );
        }
      });
    }
  }

  protected performSaveConfiguration(
    configData: ITemplateConfigurationDto | ITemplateModificationDto,
    selectedInvoice: 1 | IInvoiceTemplate
  ): Observable<boolean> {
    return this._templateConfigurationControlService.templateConfigurationControlSetConfiguration({
      IsCancellationConfig: this.isCancellationConfig(),
      Language: this.languageFormControl.value,
      TemplateConfigObj: this.isStandardFormSelected() ? null : selectedInvoice as IInvoiceTemplate,
      ConfigData: configData,
    });
  }

  getDifference(specificConfig: any, defaultConfig: any): any {
    const diff: any = {};
    console.log(specificConfig, defaultConfig);
    for (const _ in specificConfig) {
      if (specificConfig.hasOwnProperty(_)) {
        const typedKey: keyof ITemplateConfigurationFormValue = _ as keyof ITemplateConfigurationFormValue;
        if (typedKey === 'dunning') {
          continue;
        }
        // Compare Objects
        if (
          typeof specificConfig[typedKey] === 'object' &&
          specificConfig[typedKey] !== null &&
          !Array.isArray(specificConfig[typedKey])
        ) {
          const nestedDiff: any = this.getDifference(specificConfig[typedKey], defaultConfig[typedKey]);
          if (Object.keys(nestedDiff).length > 0) {
            diff[typedKey] = nestedDiff;
          }
        }
        // Compare Footers
        else if (typedKey === 'footer') {
          if (this.displayedFormRef().footerDiffersFromDefault()) {
            diff[typedKey] = specificConfig[typedKey];
          }
        }
        // Compare Arrays
        else if (
          typeof specificConfig[typedKey] === 'object' &&
          specificConfig[typedKey] !== null &&
          Array.isArray(specificConfig[typedKey])
        ) {
          if (!defaultConfig ||
            !this.displayedFormRef().areContactBlockContentsEqual(specificConfig[typedKey], defaultConfig[typedKey])) {
            diff[typedKey] = specificConfig[typedKey];
          }
        }
        // Compare Normal Form Fields
        else if (!defaultConfig || specificConfig[typedKey] !== defaultConfig[typedKey]) {
          diff[typedKey] = specificConfig[typedKey];
        }
      }
    }

    // Null all elements missing in the specificConfig
    for (const _ in defaultConfig) {
      if (defaultConfig.hasOwnProperty(_)) {
        const typedKey: keyof ITemplateConfigurationFormValue = _ as keyof ITemplateConfigurationFormValue;
        if (defaultConfig[typedKey] != null && specificConfig[typedKey] == null) {
          diff[typedKey] = null;
        }
      }
    }

    return diff;
  }

  onSelectedInvoiceChange(event: MatButtonToggleChange): void {
    // Toggles that only trigger an action carry a value of their own (e.g. the "+" that adds a
    // dunning level). Such a value must never become the selection: it would be sent on as
    // TemplateConfigObj, where the backend expects an object or null.
    if (!this.isSelectableInvoice(event.value)) {
      event.source.buttonToggleGroup.value = this.selectedInvoice();
      return;
    }

    if (typeof event.value === 'number' ? event.value === this.selectedInvoice()
      : event.value.signature === (this.selectedInvoice() as IInvoiceTemplate)?.signature) return;

    // Ask to confirm if there are unsaved changes
    if (this.displayedFormRef()?.form.dirty || this.standardFormRef()?.form.dirty) {
      this.confirmDiscardChanges().subscribe((result: boolean): void => {
        if (result) {
          this.languageReposLoaded.set(false);
          this.selectedInvoice.set(event.value);
        } else {
          event.source.buttonToggleGroup.value = this.selectedInvoice();
        }
      })
    } else {
      this.languageReposLoaded.set(false);
      this.selectedInvoice.set(event.value);
    }
  }

  protected confirmDiscardChanges(): Observable<boolean> {
    return this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Ungespeicherte Änderungen',
        description: 'Wenn Sie fortfahren, werden die Änderungen verworfen.',
        confirmButtonText: 'Fortfahren',
        cancelButtonText: 'Abbrechen',
      }
    }).afterClosed();
  }

  getSelectedSignature(): string {
    return (this.selectedInvoice() as IInvoiceTemplate)?.signature ?? null;
  }

  /**
   * Checks whether a toggle value is a valid selection: either the standard configuration sentinel
   * or a template object. Anything else belongs to an action-only toggle.
   */
  private isSelectableInvoice(value: unknown): value is IInvoiceTemplate | 1 {
    return value === 1 || (value != null && typeof value === 'object');
  }

  onResizeStart(): void {
    this.renderer.setStyle(this.overlayElement().nativeElement, 'pointer-events', 'auto');
  }

  onResizeEnd(): void {
    this.renderer.setStyle(this.overlayElement().nativeElement, 'pointer-events', 'none');
  }

  protected onLanguageSelectMouseDown(event: MouseEvent): void {
    if (this.displayedFormRef()?.form.dirty) {
      event.stopPropagation();
      event.preventDefault();
      this.confirmDiscardChanges().subscribe((result: boolean): void => {
        if (result) {
          this.displayedFormRef()?.form.markAsPristine();
          this.standardFormRef()?.form.markAsPristine();
          this.languageSelectRef()?.selector.open();
        }
      });
    }
  }

  protected createLanguageConfiguration(): void {
    // Ask to confirm if there are unsaved changes
    if (this.displayedFormRef()?.form.dirty || this.standardFormRef()?.form.dirty) {
      this.confirmDiscardChanges().subscribe((result: boolean): void => {
        if (result) {
          this.openCreateLanguageDialog();
        }
      });
    } else {
      this.openCreateLanguageDialog();
    }
  }

  private openCreateLanguageDialog(): void {
    this.dialog.open(EntitySelectorComponent, {
      data: {
        title: 'Neue Sprache konfigurieren',
        entities: this.unconfiguredLanguages().map((val: ISelectFormOption): ISelectableEntity => {
          return {displayName: val.name, id: val.name};
        }),
        showCustomCheckBox: !!this.configuredLanguages()
          .find((el: ILanguageSelectFormOption): boolean => el.usedefault),
        checkBoxText: 'Werte von Standardsprache übernehmen',
      }
    }).afterClosed().subscribe((res: any): void => {
      if (res) {
        const newLanguage: ISelectFormOption = this.unconfiguredLanguages()
          .find((el: ISelectFormOption): boolean => el.name === res.result[0]);
        this._templateConfigurationControlService.templateConfigurationControlCreateConfig({
          Language: newLanguage,
          TemplateConfigObj: this.selectedInvoice() as TDBObject,
          usedefault: res.checkBox || false,
        }).subscribe((result: boolean): void => {
          if (result) {
            if (newLanguage.value.usedefault) {
              this._alertService.invoiceAlertObjects.set(this.alertObjects()
                .filter((el: IControlObject): boolean => {
                  return el.signature !== ((this.selectedInvoice() as IInvoiceTemplate).signature);
                }));
            }
            this.requestLanguageRepos(this.selectedInvoice());
            this.languageFormControl.setValue(newLanguage);
          }
        });
      }
    });
  }

  protected isTemplateIncomplete(sig: string): boolean {
    return this.alertObjects().some((el: IControlObject): boolean => el.signature === sig);
  }

  protected addDunningLevel(): void {
    return;
  }

  protected deleteDunningLevel(): void {
    return;
  }

  protected deleteConfig(): void {
    this._templateConfigurationControlService.templateConfigurationControlDeleteConfig({
      TemplateConfigObj: this.selectedInvoice() as TDBObject,
      Language: this.languageSignal(),
    }).subscribe((res: boolean): void => {
      this.handleSuccessfulDelete(res);
    });
  }

  protected openInvoiceAutomationTab(): void {
    const tabId: string = 'invoice-automation';
    const invoice: IInvoiceTemplate | 1 = this.selectedInvoice();
    const template: IInvoiceTemplate | null = invoice !== 1 ? invoice as IInvoiceTemplate : null;

    const existing: ITab = this._tabsService.tabs().find((tab: ITab): boolean => tab.tabId === tabId);
    if (existing) {
      this._tabsService.selectTabById('Rechnungsautomation');
      const instance: InvoiceAutomationEditorComponent
        = existing.componentInstance?.instance as InvoiceAutomationEditorComponent;
      if (instance) {
        const attrIndex: number | undefined
          = (instance.apiAdapter as InvoiceAutomationApiAdapter).invoiceTemplateAttributeIndex;
        const columnFilter: IFilter['columnfilter'] = template ? [{
          attribute: 'InvoiceTemplate',
          attributeindex: attrIndex,
          expression: {filterType: 'text', type: 'equals', filter: template.name},
        }] : null;
        instance.patchFilter({columnfilter: columnFilter});
      }
    } else {
      this._invoiceAutomationTabService.pendingTemplate = template;
      this._tabsService.openTab('Rechnungsautomation', 'InvoiceAutomationEditorComponent', tabId);
    }
  }

  protected handleSuccessfulDelete(res: boolean): void {
    if (res) {
      const deletedLanguage: TDBObject = this.languageSignal();
      this.unconfiguredLanguages.set([...this.unconfiguredLanguages(), this.configuredLanguages()
        .find((el: ILanguageSelectFormOption): boolean => el.signature === deletedLanguage.signature)]);
      this.configuredLanguages.set(this.configuredLanguages()
        .filter((el: ILanguageSelectFormOption): boolean => el.signature !== deletedLanguage.signature));

      if (this.configuredLanguages().length > 0) {
        const defaultLanguage: ILanguageSelectFormOption = this.configuredLanguages()
          .find((el: ILanguageSelectFormOption): boolean => el.usedefault);
        this.languageFormControl.setValue(defaultLanguage || this.configuredLanguages()[0]);
      } else {
        this.languageFormControl.setValue(null);
      }
    }
  }
}
